/**
 * A priority queue class for generic types, utilizing a binary heap.
 *
 * @template T - The type of elements in the queue.
 */
export class PriorityQueue<T> {
  private items: T[] = [];
  private compare: (a: T, b: T) => number;

  constructor(compare: (a: T, b: T) => number) {
    this.compare = compare;
  }

  /**
   * Adds an item to the priority queue while maintaining heap properties.
   *
   * @param item - The item to be added to the queue.
   * @example
   * const pq = new PriorityQueue<number>((a, b) => a - b);
   * pq.push(3);
   * pq.push(1);
   * pq.push(4);
   */
  push(item: T) {
    this.items.push(item);
    this.heapifyUp();
  }

  /**
   * Removes and returns the smallest item from the priority queue.
   *
   * @returns The smallest item from the priority queue.
   * @throws Error if the priority queue is empty.
   * @example
   * const pq = new PriorityQueue<number>((a, b) => a - b);
   * pq.push(3);
   * pq.push(1);
   * pq.push(4);
   * const smallest = pq.pop();  // smallest will be 1
   */
  pop(): T {
    if (this.isEmpty()) {
      throw new Error('Priority queue underflow');
    }
    const smallest = this.items[0];
    const last = this.items.pop()!;
    if (!this.isEmpty()) {
      this.items[0] = last;
      this.heapifyDown();
    }
    return smallest;
  }

  /**
   * Checks if the priority queue is empty.
   *
   * @returns True if the queue is empty, false otherwise.
   * @example
   * const pq = new PriorityQueue<number>((a, b) => a - b);
   * const empty = pq.isEmpty();  // empty will be true
   */
  isEmpty(): boolean {
    return this.items.length === 0;
  }

  /**
   * Restores heap properties upwards from the last element.
   * This is a private method and should not be called directly.
   */
  private heapifyUp() {
    let index = this.items.length - 1;
    while (index > 0) {
      const parentIndex = Math.floor((index - 1) / 2);
      if (this.compare(this.items[index], this.items[parentIndex]) < 0) {
        [this.items[index], this.items[parentIndex]] = [this.items[parentIndex], this.items[index]];
        index = parentIndex;
      } else {
        break;
      }
    }
  }

  /**
   * Restores heap properties downwards from the root element.
   */
  private heapifyDown() {
    let index = 0;
    const length = this.items.length;

    while (true) {
      const leftIndex = 2 * index + 1;
      const rightIndex = 2 * index + 2;
      let smallerIndex = index;

      if (leftIndex < length && this.compare(this.items[leftIndex], this.items[smallerIndex]) < 0) {
        smallerIndex = leftIndex;
      }

      if (rightIndex < length && this.compare(this.items[rightIndex], this.items[smallerIndex]) < 0) {
        smallerIndex = rightIndex;
      }

      if (smallerIndex !== index) {
        [this.items[index], this.items[smallerIndex]] = [this.items[smallerIndex], this.items[index]];
        index = smallerIndex;
      } else {
        break;
      }
    }
  }
}
